// Servizio Preventivi (quotes) — collezione Firestore users/{uid}/quotes.
//
// Modello dati Quote:
// {
//   id: string,
//   number: string,               // Numero preventivo (es. "PR-01-2026")
//   clientId: string | null,      // id cliente (anagrafica), se selezionato da autocomplete
//   clientName: string,           // Nome cliente (sempre presente)
//   items: [{ description, quantity, unitPrice, total }],
//   subtotal: number,
//   tax: number,                  // Importo imposta/ritenuta (opzionale, default 0)
//   total: number,                // subtotal + tax
//   status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'converted',
//   validUntil: string,           // "YYYY-MM-DD"
//   issueDate: string,            // "YYYY-MM-DD"
//   notes: string,
//   convertedInvoiceId: string | null, // valorizzato dopo la conversione in fattura
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }
//
// NOTA per Fatture-Dev: i preventivi con status === 'accepted' sono quelli pronti per
// essere convertiti in fattura (azione manuale dall'utente in QuotesPage). Dopo la
// conversione lo status passa a 'converted' e viene salvato convertedInvoiceId.
// Per leggere i preventivi in tempo reale usa subscribeQuotes(uid, cb) e filtra
// client-side per status (Firestore non richiede un indice aggiuntivo per il solo
// orderBy su createdAt).

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";

export const QUOTE_STATUSES = [
  { id: "draft", label: "Bozza" },
  { id: "sent", label: "Inviato" },
  { id: "accepted", label: "Accettato" },
  { id: "rejected", label: "Rifiutato" },
  { id: "expired", label: "Scaduto" },
  { id: "converted", label: "Convertito" },
];

function quotesCollection(uid) {
  return collection(db, "users", uid, "quotes");
}

// Normalizza una riga item: total = quantity * unitPrice (arrotondato a 2 decimali).
function normalizeItem(item) {
  const quantity = Number(item.quantity) || 0;
  const unitPrice = Number(item.unitPrice) || 0;
  const total = Math.round(quantity * unitPrice * 100) / 100;
  return {
    description: (item.description || "").trim(),
    quantity,
    unitPrice,
    total,
  };
}

function normalizeQuote(data) {
  const items = Array.isArray(data.items) ? data.items.map(normalizeItem) : [];
  const subtotal = Math.round(items.reduce((sum, it) => sum + it.total, 0) * 100) / 100;
  const tax = Number(data.tax) || 0;
  const total = Math.round((subtotal + tax) * 100) / 100;

  return {
    number: (data.number || "").trim(),
    clientId: data.clientId || null,
    clientName: (data.clientName || "").trim().replace(/\s+/g, " "),
    items,
    subtotal,
    tax,
    total,
    status: data.status || "draft",
    validUntil: data.validUntil || "",
    issueDate: data.issueDate || new Date().toISOString().split("T")[0],
    notes: (data.notes || "").trim(),
    convertedInvoiceId: data.convertedInvoiceId || null,
  };
}

// Sottoscrizione in real-time ai preventivi dell'utente, ordinati per data di
// creazione decrescente. Ritorna la funzione di unsubscribe.
export function subscribeQuotes(uid, callback) {
  if (!uid) return () => {};
  const q = query(quotesCollection(uid), orderBy("createdAt", "desc"));
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn("quotes snapshot error:", err?.message || err);
      callback([]);
    }
  );
}

export async function createQuote(uid, data) {
  const docRef = await addDoc(quotesCollection(uid), {
    ...normalizeQuote(data),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

export async function updateQuote(uid, id, data) {
  const docRef = doc(db, "users", uid, "quotes", id);
  await updateDoc(docRef, {
    ...normalizeQuote(data),
    updatedAt: serverTimestamp(),
  });
}

// Update "leggero": aggiorna solo i campi passati senza rinormalizzare l'intero
// preventivo (utile per patch minime come lo status dopo la conversione in fattura).
export async function patchQuote(uid, id, patch) {
  const docRef = doc(db, "users", uid, "quotes", id);
  await updateDoc(docRef, {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteQuote(uid, id) {
  const docRef = doc(db, "users", uid, "quotes", id);
  await deleteDoc(docRef);
}

// Costruisce il draft fattura a partire da un preventivo accettato. La creazione
// effettiva della fattura è responsabilità del componente chiamante, tramite
// createInvoice(draft) di useAppState (non scrivere mai le fatture da qui).
export function buildInvoiceDraftFromQuote(quote) {
  const today = new Date().toISOString().split("T")[0];
  return {
    issueDate: today,
    clientName: quote.clientName || "",
    clientId: quote.clientId || undefined,
    grossAmount: Number(quote.total) || 0,
    paid: false,
    notes: quote.notes
      ? `Da preventivo ${quote.number || ""}. ${quote.notes}`.trim()
      : `Da preventivo ${quote.number || ""}`.trim(),
  };
}
